"use client"

import type React from "react"

import { useState, forwardRef, createContext, useContext, useMemo, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { SearchableDropdown } from "@/components/ui/searchable-dropdown"
import { cn } from "@/lib/utils"
import { ChevronDown } from "lucide-react"
import { ClearButton } from "@/components/ui/clear-button"

// Context to pass validation attributes and ref from wrapper to button
const VirtualizedSelectContext = createContext<{
  ref?: React.Ref<HTMLButtonElement>
  id?: string
  "data-show-error"?: boolean
  "aria-invalid"?: boolean
  "aria-describedby"?: string
  onOpenChange?: (open: boolean) => void
}>({})

// VirtualizedSelect component - searchable dropdown with input field styling
interface VirtualizedSelectProps<T = any> {
  options: T[]
  value?: string
  onValueChange: (value: string) => void
  // Mapping helpers
  getItemLabel?: (item: T) => string
  getItemValue?: (item: T) => string
  // UI & behaviour
  id?: string
  className?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  searchable?: boolean
  /** When true, shows an "x" affordance to clear the current selection (emits an empty string). */
  allowClear?: boolean
  onBlur?: () => void
  // Form validation
  "aria-invalid"?: boolean
  "data-show-error"?: boolean
  "aria-describedby"?: string
  // Custom rendering
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode
  renderSelectedValue?: (item: T) => React.ReactNode
}

const VirtualizedSelectInner = <T,>({
    options,
    value,
    onValueChange,
    getItemLabel = (item: T) => String(item),
    getItemValue = (item: T) => String(item),
    className,
    placeholder = "Select an option...",
    searchPlaceholder = "Search options...",
    emptyMessage,
    disabled = false,
    searchable = true,
    allowClear = false,
    renderItem,
    renderSelectedValue,
  }: Omit<VirtualizedSelectProps<T>, 'id' | 'aria-invalid' | 'data-show-error' | 'onBlur'>
) => {
  const [open, setOpen] = useState(false)
  const context = useContext(VirtualizedSelectContext)

  // Get selected option for display
  const selectedOption = options.find((option) => getItemValue(option) === value)

  // Show the clear affordance only when clearing is enabled, a value is set, and the control is editable.
  const showClear = allowClear && !!value && !disabled

  // Handle open change - call context handler and update local state
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    context.onOpenChange?.(newOpen)
  }

  const triggerButton = (
    <PopoverTrigger asChild>
      <Button
        ref={context.ref}
        id={context.id}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        aria-invalid={context["aria-invalid"]}
        aria-describedby={context["aria-describedby"]}
        data-show-error={context["data-show-error"]}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
          "data-[show-error]:aria-[invalid=true]:border-destructive data-[show-error]:aria-[invalid=true]:ring-destructive",
          showClear && "pr-12",
          className
        )}
        disabled={disabled}
      >
        {selectedOption ? (
          renderSelectedValue ? renderSelectedValue(selectedOption) : (
            <span>{getItemLabel(selectedOption)}</span>
          )
        ) : (
          placeholder
        )}
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
  )

  return (
    <Popover open={open && !disabled} onOpenChange={handleOpenChange}>
      {/* Only introduce the positioning wrapper when the clear affordance is rendered, so
          non-clearable usages keep their original DOM/layout. */}
      {showClear ? (
        <div className="relative w-full">
          {triggerButton}
          <ClearButton onClear={() => onValueChange("")} />
        </div>
      ) : triggerButton}
      <PopoverContent className="w-full p-1" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <SearchableDropdown
          options={options}
          value={value}
          onValueChange={(value) => {
            onValueChange(value)
            handleOpenChange(false)
          }}
          getItemLabel={getItemLabel}
          getItemValue={getItemValue}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          searchable={searchable}
          renderItem={renderItem}
        />
      </PopoverContent>
    </Popover>
  )
}

// Wrapper component that accepts ref and validation props, passes via context
const VirtualizedSelectWrapper = <T,>(
  { id, "data-show-error": showError, "aria-invalid": invalid, "aria-describedby": describedBy, onBlur, ...props }: VirtualizedSelectProps<T>,
  ref: React.Ref<HTMLButtonElement>
) => {
  // Handle open change - trigger blur when closing
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open && onBlur) {
      onBlur()
    }
  }, [onBlur])

  const contextValue = useMemo(() => ({
    ref,
    id,
    "data-show-error": showError,
    "aria-invalid": invalid,
    "aria-describedby": describedBy,
    onOpenChange: handleOpenChange
  }), [ref, id, showError, invalid, describedBy, handleOpenChange])

  return (
    <VirtualizedSelectContext.Provider value={contextValue}>
      <VirtualizedSelectInner {...props} />
    </VirtualizedSelectContext.Provider>
  )
}

// Use forwardRef with generic component
const VirtualizedSelect = forwardRef(VirtualizedSelectWrapper) as <T = any>(
  props: VirtualizedSelectProps<T> & { ref?: React.Ref<HTMLButtonElement> }
) => ReturnType<typeof VirtualizedSelectWrapper>

export { VirtualizedSelect }